import React, {useEffect, useRef, useState} from "react";
import {message, Table, Button, Switch, Select, Popconfirm} from "antd";
import {
    MenuOutlined,
} from '@ant-design/icons';
import {request} from "../../util/request";
import {GLOBAL_CONNECTION_API, GLOBAL_BOSS_API} from "../../config/api";
import {insertToClipboard, ObjTest} from "jzfz-web-utils";
import RenameModal from "./RenameModal";
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import Uploader from "./Uploader";
import {getAttachmentHtml} from "../tinymceEditor/AttachmentElement";
import DownloadRecordModal from "./DownloadRecordModal";
import {openAttachmentPreviewer} from "./utils";
import {downloadAttachment, getAttachmentPreviewURL} from '../../api/commonAPI';
import './style/style.less';
import ContentAuth from "../contentAuth/ContentAuth";
import {ModuleEnum} from "../../config/constant";
import {AttachmentDataSource, AttachmentDownloadScopeEnum} from "./interface";


const { Option } = Select;
const SortableItem = SortableElement(props => <tr {...props} />);
const SortableBody = SortableContainer(props => <tbody {...props} />);
const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />);

// 下载下拉
const downloadOptions = [
    {label: '部分人可下载', value: AttachmentDownloadScopeEnum.SOMEBODY},
    {label: '禁止下载', value: AttachmentDownloadScopeEnum.NOBODY},
    {label: '所有人可下载', value: AttachmentDownloadScopeEnum.EVERYBODY},
]


export interface AttachmentUploaderProps {
    pkid?: string;  // 当前知识pkid
    style?: any;
    value?: AttachmentDataSource[];
    hiddenNames: string[];    // 需要隐藏的附件字段名集合
    beforeUpload?: (file: any, fileList: any[]) => boolean;    // 上传文件之前的钩子
    onChange?: (value: any[]) => void;
}


/**
 * 附件上传组件
 * @param props
 * @constructor
 */
const AttachmentUploader = (props: AttachmentUploaderProps) => {
    const { pkid, style, value = [], hiddenNames = [], onChange = (value: any) => {}, beforeUpload = () => true } = props;

    // 附件列表
    const [loading, setLoading] = useState<boolean>(false);
    // 附件下载信息
    // 重命名弹窗
    const [renameFormData, setRenameFormData] = useState<any>({});
    const [renameModalVisible, setRenameModalVisible] = useState<boolean>(false);
    // 下载记录弹窗
    const [downloadRecordVisible, setDownloadRecordVisible] = useState<boolean>(false);
    const [downloadId, setDownloadId] = useState<string>('');
    // 下载配置弹窗
    const [downloadConfigVisible, setDownloadConfigVisible] = useState<boolean>(false);
    const [downloadConfigLoading, setDownloadConfigLoading] = useState<boolean>(false);
    const [downloadConfig, setDownloadConfig] = useState<any>({});

    // 文件数量
    const countRef = useRef<number>(0);


    useEffect(() => {
        return () => {
            countRef.current = 0;   // 文件数量归零
        }
    }, [])


    /**
     * 预览
     * @param data
     */
    const previewHandle = (data: any) => {
        const { fileName, attachmentId } = data;

        setLoading(true);
        getAttachmentPreviewURL(attachmentId).then((res: any) => {
            openAttachmentPreviewer({url: res.data, fileName});
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 更新预览、下载状态
     * @param params
     */
    const updateStatus = (params: {attachmentId, downloadScope, previewable}) => {
        const { attachmentId, downloadScope, previewable } = params;

        setLoading(true);
        request('/attachment/downloadable/config', params, {baseURL: GLOBAL_CONNECTION_API}).then((res: any) => {
            let newData = value.map((v: any) => v.attachmentId === attachmentId ? {...v, downloadScope, previewable} : v);
            onChange(newData);
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 预览开关切换
     * @param data
     */
    const previewChange = (data: any) => {
        const { attachmentId, downloadScope, previewable } = data;
        let params = {
            attachmentId,
            downloadScope: !previewable === false ? AttachmentDownloadScopeEnum.EVERYBODY : downloadScope,
            previewable: !previewable,
        }
        updateStatus(params);
    }


    /**
     * 复制
     * @param data
     */
    const copyHandle = (data: any) => {
        let html = getAttachmentHtml(data);
        insertToClipboard(html);
        message.success('复制成功');
    }


    /**
     * 重命名
     * @param data
     */
    const renameHandle = (data: any) => {
        setRenameModalVisible(true);
        setRenameFormData({...data});
    }


    /**
     * 下载
     * @param data
     */
    const downloadHandle = (data: any) => {
        const { attachmentId } = data;

        setLoading(true);
        downloadAttachment(attachmentId).then((res: any) => {
            message.success('下载成功');
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 显示全部下载按钮
     */
    const showDownloadAll = (): boolean => {
        if (ObjTest.isNotEmptyArray(value) && value.some((v: any) => v.downloadable || !isRef(v))) {
            return true;
        }
        return false;
    }


    /**
     * 批量下载
     */
    const downloadAll = () => {
        let ids = value.filter((v: any) => v.downloadable).map((v: any) => v.attachmentId);
        if (!ObjTest.isNotEmptyArray(ids)) {
            message.warning('没有可下载的附件');
            return;
        }
        ids.forEach((v: any) => {
            downloadAttachment(v);
        })
    }


    /**
     * 删除
     * @param data
     * @param isRef 是否引用
     */
    const removeHandle = (data: any, isRef: boolean) => {
        const { attachmentId, downloadable, uploadKnowledgeId } = data;
        if (isRef) {
            let newData = [...value].filter((v: any) => v.attachmentId !== attachmentId);
            onChange(newData);
            return;
        }
        setLoading(true);
        request('/attachment/delete', {pkId: attachmentId}, {baseURL: GLOBAL_CONNECTION_API}).then((res: any) => {
            message.success('删除成功');
            let newData = [...value].filter((v: any) => v.attachmentId !== attachmentId);
            onChange(newData);
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 判断附件是否是引用的
     * @param data
     */
    const isRef = (data: any): boolean => {
        const { uploadKnowledgeId } = data;
        return (uploadKnowledgeId && (pkid || null) !== (uploadKnowledgeId || null));
    }


    const getBtns = (record: any): React.ReactNode[] => {
        const { uploadKnowledgeId, downloadable } = record;
        let btns = [];
        let refFlag = isRef(record);
        // 非引用的附件
        if (!refFlag) {
            btns = [
                ...btns,
                <Button className="link-btn f12" type="link" size="small" onClick={() => copyHandle(record)}>复制</Button>,
                <Button className="link-btn f12" type="link" size="small" onClick={() => renameHandle(record)}>修改名称</Button>,
            ]
        }
        // 允许下载
        if (downloadable || !isRef(record)) {
            btns = [
                ...btns,
                <Button className="link-btn f12" type="link" size="small" onClick={() => downloadHandle(record)}>下载</Button>,
            ]
        }
        btns = [
            ...btns,
            <Popconfirm
                title="确认要删除么?"
                okText="确认"
                cancelText="取消"
                placement="topRight"
                align={{offset: [0, 5]}}
                okButtonProps={{className: 'button-red'}}
                cancelButtonProps={{className: 'button-default'}}
                onConfirm={() => removeHandle(record, refFlag)}
            >
                <Button className="link-btn f12" type="link" size="small">{refFlag ? '删除引用' : '删除'}</Button>
            </Popconfirm>,
        ]

        return btns;
    }


    /**
     * 选择下载项
     * @param data
     * @param record
     */
    const selectDownload = (downloadScope: string, record: any) => {
        const { attachmentId, previewable } = record;
        updateStatus({attachmentId, downloadScope, previewable});
    }


    /**
     * 显示配置弹窗
     * @param record
     */
    const showConfig = (record: any) => {
        const { attachmentId } = record;
        let params = {appType: ModuleEnum.attachment, knowledgeId: attachmentId};
        setDownloadConfigVisible(true);
        setDownloadConfigLoading(true);
        request('/knowledge-authorization-config/get', params, {method: 'get', baseURL: GLOBAL_BOSS_API}).then((res: any) => {
            setDownloadConfig({...(res.data || {}), ...params});
        }).finally(() => {
            setDownloadConfigLoading(false);
        })
    }


    const columns: any[] = [
        {
            className: 'drag-visible',
            title: '排序',
            width: 40,
            align: 'center',
            render: () => <DragHandle />,
        },
        {
            className: 'drag-visible',
            title: (<div className="tc">文件名</div>),
            dataIndex: 'fileName',
            ellipsis: true,
        },
        {
            title: '预览',
            dataIndex: 'previewable',
            align: 'center',
            width: 100,
            render: (text: any, record: any, index: number) => {
                // supportPreview: 支不支持预览  previewable: 是否可以预览
                const { supportPreview, previewable, uploadKnowledgeId } = record;
                // 附件所属知识id跟当前知识id相同
                if (!uploadKnowledgeId || (uploadKnowledgeId || null) === (pkid || null)) {
                    return supportPreview ? (
                        <>
                            <Switch
                                size="small"
                                checked={text}
                                onChange={() => previewChange(record)}
                            />
                            <span className="ml10">{text ? '允许预览' : '禁止预览'}</span>
                        </>
                    ) : (
                        <span className="ml10">不支持预览</span>
                    )
                }

                if (!supportPreview) {
                    return <span>不支持预览</span>
                }

                return text ? (
                    <Button
                        className="link-btn f12"
                        type="link"
                        size="small"
                        title="点击预览"
                        onClick={() => previewHandle(record)}
                    >可预览
                    </Button>
                ) : <span>预览已禁用</span>
            }
        },
        {
            title: '下载限制',
            dataIndex: 'downloadable',
            align: 'center',
            width: 150,
            render: (text: boolean, record: any, index: number) => {
                const { downloadScope } = record;
                return (
                    <div className="w h flexic">
                        <Select
                            placeholder="请选择"
                            className="flex1 f12"
                            style={{width: 110}}
                            value={downloadScope}
                            size="small"
                            bordered={false}
                            dropdownMatchSelectWidth={false}
                            onSelect={(data: any) => selectDownload(data, record)}
                        >
                            {
                                downloadOptions.map((v: any, i: number) => (
                                    <Option value={v.value} key={i}>{v.label}</Option>
                                ))
                            }
                        </Select>
                        {
                            downloadScope === AttachmentDownloadScopeEnum.SOMEBODY && (
                                <span className="ml5 link-btn f12" onClick={() => showConfig(record)}>配置</span>
                            )
                        }
                    </div>
                )
            }
        },
        {
            title: '大小',
            dataIndex: 'size',
            width: 80,
            align: 'center',
            ellipsis: true,
        },
        {
            title: (<div className="tc">上传用户</div>),
            dataIndex: 'uploader',
            width: 120,
            ellipsis: true,
            render: (text: any, record: any, index: number) => {
                const { userName, userId } = text || {};
                return `${userName}(${userId})`
            }
        },
        {
            title: (<div className="tc">上传日期</div>),
            dataIndex: 'uploadTime',
            width: 120,
        },
        {
            title: '下载次数',
            dataIndex: 'downloadCount',
            align: 'center',
            width: 60,
            ellipsis: true,
            render: (text: number, record: any, index: number) => {
                const { attachmentId } = record;
                return (
                    <Button
                        className="link-btn f12"
                        block={true}
                        type="link"
                        size="small"
                        title="点击查看下载历史"
                        onClick={() => {
                            setDownloadRecordVisible(true);
                            setDownloadId(attachmentId);
                        }}
                    >{text}</Button>
                )
            }
        },
        {
            title: (
                <div className="flexic">
                    <span>操作</span>
                    {
                        showDownloadAll() && (
                            <Button
                                className="mla link-btn f12"
                                type="link"
                                size="small"
                                onClick={() => downloadAll()}
                            >全部下载</Button>
                        )
                    }
                </div>
            ),
            width: 180,
            render: (text: string, record: any, index: number) => {
                return (
                    getBtns(record).map((v: any, i: number) => (
                        <React.Fragment key={i}>
                            { i > 0 && <span className="split-line">|</span> }
                            {v}
                        </React.Fragment>
                    ))
                )
            }
        },
    ]


    /**
     * 获取列
     */
    const getColumns = (): any[] => {
        if (ObjTest.isNotEmptyArray(hiddenNames)) {
            return columns.filter((v: any) => !hiddenNames.includes(v.dataIndex));
        }
        return columns;
    }


    const onSortEnd = ({ oldIndex, newIndex }) => {
        let newData = arrayMove([...value], oldIndex, newIndex);
        onChange(newData);
    }


    const getDraggableContainer = props => (
        <SortableBody
            useDragHandle
            helperClass="attachment-table-row-dragging"
            onSortEnd={onSortEnd}
            {...props}
        />
    );


    const getDraggableBodyRow = ({ className, style, ...restProps }) => {
        const index = value.findIndex(x => x.attachmentId === restProps['data-row-key']);
        return <SortableItem index={index} {...restProps} />;
    };


    /**
     * 权限配置表单提交
     * @param values
     */
    const authConfigSumit = (values: any) => {
        setDownloadConfigLoading(true);
        request('/knowledge-authorization-config/save', values, {baseURL: GLOBAL_BOSS_API}).then((res: any) => {
            setDownloadConfigVisible(false);
            message.success('权限配置成功');
        }).finally(() => {
            setDownloadConfigLoading(false);
        })
    }


    return (
        <div className="custom-attachment-uploader" style={style}>
            <Uploader
                type="dragger"
                beforeUpload={beforeUpload}
                onChange={(files: any[]) => onChange([...value, ...files])}
            />
            <Table
                className="attachment-table mt15"
                rowKey="attachmentId"
                bordered
                loading={loading}
                dataSource={value}
                columns={getColumns()}
                sticky={true}
                pagination={false}
                components={{
                    body: {
                        wrapper: getDraggableContainer,
                        row: getDraggableBodyRow,
                    },
                }}
                scroll={{x: 900}}
            />
            <RenameModal
                visible={renameModalVisible}
                formData={renameFormData}
                onSubmit={(values: any) => {
                    setRenameModalVisible(false);
                    // 更新附件名称
                    const { attachmentId, fileName } = values;
                    let newData = [...value];
                    let index = newData.findIndex((v: any) => v.attachmentId === attachmentId);
                    if (index > -1) {
                        let rowData = newData[index];
                        rowData.fileName = fileName;
                        onChange(newData);
                    }
                }}
                onCancel={() => setRenameModalVisible(false)}
            />
            <DownloadRecordModal
                visible={downloadRecordVisible}
                attachmentId={downloadId}
                onCancel={() => setDownloadRecordVisible(false)}
            />
            <ContentAuth
                title="权限配置"
                visible={downloadConfigVisible}
                loading={downloadConfigLoading}
                content={downloadConfig}
                onClose={() => setDownloadConfigVisible(false)}
                onSubmit={authConfigSumit}
            />
        </div>
    )
}


export default AttachmentUploader;
